import {apiSlice} from './apiSlice';
import dayjs from 'dayjs';
import {
    AvailabilityResponse,
    CarMapDeployable,
    Coordinates,
    DeploymentMeta,
    GetDeploymentsOnlyAvailableParams,
    GetDeploymentsProps,
    LocationCarsList,
    DeploymentsWithAvailabilityResponse
} from '../../types/deploymentTypes';
import {DeploymentIndexReturn} from '../../types/bookingTypes';
import {formatTimestamp} from '../../helpers/misc';

export const deploymentInjectedEndpoints = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getDeploymentsIncludeAvailability: builder.query<LocationCarsList, GetDeploymentsProps>({
            query: ({startedAt, endedAt, page = 1, pageSize = 999999}) => {
                const formattedStartedAt = startedAt ? formatTimestamp(startedAt) : formatTimestamp(dayjs().tz());
                const formattedEndedAt = endedAt ? formatTimestamp(dayjs(endedAt).tz().add(1, 's')) : null;

                let indexUrl = `/deployments?page=${encodeURIComponent(page)}&per_page=${encodeURIComponent(pageSize)}&filter_type=${encodeURIComponent('include_availability')}`;
                indexUrl += `&started_at=${encodeURIComponent(formattedStartedAt)}`;

                if (formattedEndedAt) {
                    indexUrl += `&ended_at=${encodeURIComponent(formattedEndedAt)}`;
                }
                return indexUrl;
            },
            providesTags: ['Deployments'],
            transformResponse: (response: DeploymentsWithAvailabilityResponse, _, args) => {
                const noStartOrEndTime = !args.startedAt && !args.endedAt;
                const currentLocationList: LocationCarsList = {};

                response.data.forEach((deployment) => {
                    let car: CarMapDeployable = {
                        // See Deployable interface for all vehicle data coming from response.
                        'id': deployment.deployable.id,
                        'name': `${deployment.deployable.make} ${deployment.deployable.model}`,
                        'class': deployment.deployable.class,
                        'category': deployment.deployable.category,
                        'colour': deployment.deployable.colour,
                        'seats': deployment.deployable.seats,
                        'description': deployment.deployable_description.toString(),
                        'deployment_id': deployment.deployment_id, // Will be null if deployment is unavailable.
                        'car_is_available': noStartOrEndTime || !!deployment.deployment_id, // Needed to indicate availability on drawer open.
                            // TODO: This needs to be looked at, as it's not a reliable way of seeing the selected date range deployment availability.
                        'image': deployment.image?.toString() ?? null,
                    };

                    const locationId = deployment.location_id.toString();

                    const lat = deployment.location.latitude;
                    const long = deployment.location.longitude;
                    const coords: Coordinates = lat != null && long != null ? [long, lat] : null;

                    // If a carLocation object exists, add car to its deployments array. Otherwise, create new.
                    if (currentLocationList[locationId]) {
                        currentLocationList[locationId].deployments.push(car);

                        // Depending on if all cars are available/unavailable at a location
                        // we set the value partial to indicate that it is a mixed availability cluster.
                        // Otherwise, all cars will be available/unavailable and the location.is_available stays the same.
                        if (currentLocationList[locationId].is_available !== car.car_is_available) {
                            currentLocationList[locationId].is_available = 'partial';
                        }
                    } else {
                        currentLocationList[locationId] = {
                            'id': deployment.location_id,
                            'location': {
                                'id': deployment.location_id,
                                'name': deployment.location.name,
                                'description': deployment.location.description,
                                'coordinates':  coords,
                            },
                            'deployments': [car],
                            'is_available': noStartOrEndTime || !!deployment.deployment_id,
                            'name': '',
                            'latitude': 0,
                            'longitude': 0,
                            'class': '',
                            'category': '',
                            'colour': '',
                            'description': '',
                            'seats': 0,
                            'deployment_id': 0,
                            'car_is_available': false,
                        };
                    }
                });
                return currentLocationList;
            },
        }),
        getDeploymentsOnlyAvailable: builder.query<DeploymentIndexReturn[], GetDeploymentsOnlyAvailableParams>({
            query: ({startedAt, endedAt, page = 1, pageSize = 999999}) => {
                // Filter type refers to the conditionals in the DeploymentController:index route.
                // 'only_available' is used for the DeploymentSelect.
                const filterType = 'only_available';

                const formattedStartedAt = formatTimestamp(dayjs(startedAt).tz());
                const encodedPage = encodeURIComponent(page);
                const encodedPageSize = encodeURIComponent(pageSize);

                let indexUrl = `/deployments?page=${encodedPage}&per_page=${encodedPageSize}&filter_type=${filterType}&started_at=${encodeURIComponent(formattedStartedAt)}`;

                if (endedAt) {
                    const formattedEndedAt = formatTimestamp(dayjs(endedAt).tz());
                    indexUrl += `&ended_at=${encodeURIComponent(formattedEndedAt)}`;
                }

                return indexUrl;
            },
            transformResponse: (response: DeploymentMeta) => response.data,
            providesTags: ['Deployments'],
        }),
        getAvailabilityByDeployment: builder.query<AvailabilityResponse[], number>({
            query: (deploymentID) => `/deployments/${deploymentID}/availability`,
            providesTags: ['Deployments'],
        }),
    }),
});

export const deploymentEndpoints = deploymentInjectedEndpoints.endpoints;
export const {
    useGetDeploymentsIncludeAvailabilityQuery,
    useGetAvailabilityByDeploymentQuery,
    useGetDeploymentsOnlyAvailableQuery
} = deploymentInjectedEndpoints;
